import { useSortable } from '@dnd-kit/sortable';
import { CSSProperties, useEffect, useRef } from 'react';
import useWindowDimensions from '../../Responsive/Hooks/useWindowDimensionsHook';
import styles from './Table.module.scss';
import { stickyColumnStyle, TableHeader } from './TableDefinitions';

interface ITableHeaderCellProps<T> {
    tableHeader: TableHeader<T>;
    tallestHeaderHeight: number;
    setTallestHeaderHeight: (height: number) => void;
    isColumnOrderingEnabled?: boolean;
    hasGroupHeaders?: boolean;
}

const defaultPixelBufferForHeader = 4;

// eslint-disable-next-line @typescript-eslint/comma-dangle
const TableHeaderCell = <T,>(props: ITableHeaderCellProps<T>) => {
    // Application hooks and variables.
    const windowDimensions = useWindowDimensions();
    const elementReference = useRef<HTMLDivElement>(null);
    const dataFormatIsNumber = props.tableHeader.dataFormat === 'currency' || props.tableHeader.dataFormat === 'percent';
    const stickyStyles = props.tableHeader.pinned === 'left' && ({ ...stickyColumnStyle, left: 16, zIndex: 4 } as CSSProperties);

    // useSortable is always called (Rules of Hooks). Drag is only active when isColumnOrderingEnabled
    // is true AND the column is not pinned. Pinned columns remain fixed regardless of drag.
    const isDraggable = !!props.isColumnOrderingEnabled && !props.tableHeader.pinned;
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: String(props.tableHeader.field ?? props.tableHeader.header ?? ''),
        disabled: !isDraggable,
    });

    useEffect(() => {
        const elementsNumbers = document.getElementsByClassName(styles.table_header_number);
        const elementsText = document.getElementsByClassName(styles.table_header_text);
        const arrayOfNumbersHeights = Array.prototype.map.call(elementsNumbers, (x) => x.clientHeight ?? 0) as number[];
        const arrayOfTextHeights = Array.prototype.map.call(elementsText, (x) => x.clientHeight ?? 0) as number[];
        const minimumHeaderHeight = Math.max(...arrayOfNumbersHeights, ...arrayOfTextHeights) + defaultPixelBufferForHeader;

        props.setTallestHeaderHeight(minimumHeaderHeight);
    }, [elementReference.current?.clientHeight, windowDimensions]);

    return (
        <div
            ref={isDraggable ? setNodeRef : undefined}
            className={`${styles.table_header}${isDraggable ? ` ${styles.table_header_draggable}` : ''}`}
            style={{
                ...props.tableHeader.cellStyle,
                width: props.tableHeader.width,
                minWidth: props.tableHeader.minimumWidth,
                height: `${props.tallestHeaderHeight}px`,
                justifyContent: dataFormatIsNumber ? 'end' : 'start',
                opacity: isDragging && props.isColumnOrderingEnabled ? 0 : 1,
                top: props.hasGroupHeaders ? '40px' : '0px',
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(isDraggable ? attributes : {})}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(isDraggable ? listeners : {})}
        >
            <div ref={elementReference} style={{ ...stickyStyles }} className={dataFormatIsNumber ? styles.table_header_number : styles.table_header_text}>
                {props.tableHeader.headerValueGetter ? props.tableHeader.headerValueGetter() : props.tableHeader.header}
            </div>
        </div>
    );
};

export default TableHeaderCell;
